import { Component, computed, Input, OnInit, Signal, signal } from "@angular/core";
import { Comment, CommentLocation } from "@orion2/models/tocitem.models";
import { MatCardModule } from "@angular/material/card";
import { MatIconModule } from "@angular/material/icon";
import { MatMenuModule } from "@angular/material/menu";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
  ConfirmDialogResult
} from "libs/components/confirm-dialog/confirm-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { Store } from "@viewer/core";
import { CommentService } from "@viewer/toc-items/comments-module/services/comment.service";
import { CommentEditComponent } from "@viewer/toc-items/comments-module//comment-edit/comment-edit.component";
import { MatButtonModule } from "@angular/material/button";

import { CompactedDmcPipe } from "@viewer/core/pipe/compacted-dmc.pipe";
import { Router } from "@angular/router";
import { CommentStatus, CommentTypes } from "@viewer/shared-module/constant";
import { MaterialFileInputModule } from "ngx-custom-material-file-input";

@Component({
  standalone: true,
  selector: "o-comment",
  imports: [
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule,
    CommentEditComponent,
    CompactedDmcPipe,
    MaterialFileInputModule
  ],
  templateUrl: "./comment.component.html",
  styleUrl: "./comment.component.scss"
})
export class CommentComponent implements OnInit {
  @Input() comment: Comment;
  @Input() editMode = false;
  @Input() expanded = false;

  public formattedDate: string;
  public readonly isOnDm = signal(false);
  public readonly reference = signal("");
  public readonly commentLocation = CommentLocation;
  public isDuplicateComputed: Signal<boolean>;

  // Allows you to retrieve the complete value of a comment type.
  public readonly commentTypes = CommentTypes;

  // Allows you to retrieve the corresponding value of a comment status.
  public readonly commentStatus = CommentStatus;

  // If this comment can be managed by the current user.
  public readonly canManage = computed<boolean>(() => this.commentService.canManage(this.comment));
  /*
    If the user can edit a comment.
    SPEC: Only Q comments can be edited.
  */
  public readonly canEdit = computed(() => this.canManage() && this.comment.commentType === "q");

  constructor(
    public readonly store: Store,
    public readonly commentService: CommentService,
    private readonly translate: TranslateService,
    private readonly dialog: MatDialog,
    private readonly router: Router
  ) {}

  ngOnInit() {
    this.formattedDate = new Date(this.comment.date).toLocaleString(this.store.currentLanguage);
    if (!this.comment.title || this.comment.fromNote) {
      this.editMode = true;
    }
    this.reference.set(this.comment.reference.replace(/#/g, " "));

    /**
     * We do not want to display the comment reference if it relates to a DM
     * and we are currently on the DM.
     * We do that to be coherent with the notes (same behaviour).
     */
    this.isOnDm.set(/\/du\//.test(this.router.url));
    this.isDuplicateComputed = computed(() =>
      this.commentService.isDuplicateCode(this.comment._id, this.comment.code)
    );
  }

  /**
   * Opens a confirmation modal.
   * After confirmation, deletes the current comment.
   * @public
   * @returns {void}
   */
  public delete(): void {
    if (!this.canManage()) {
      return;
    }

    if (!this.comment.title || this.comment.fromNote) {
      // In this case, the comment isn't save in DB, no need to confirm deletion
      // To be consistend with what is done when user cancel editing of creating comment
      this.commentService.deleteComments([this.comment]);
      return;
    }
    const data: ConfirmDialogData = {
      message: [
        this.translate.instant("folders.delete", {
          name: this.comment.code
        })
      ],
      confirmLabels: [this.translate.instant("continue")],
      cancelLabels: [this.translate.instant("cancel")]
    };

    this.dialog
      .open(ConfirmDialogComponent, { data })
      .afterClosed()
      .subscribe((result: ConfirmDialogResult) => {
        if (result.confirm) {
          this.commentService.deleteComments([this.comment]);
        }
      });
  }

  /**
   * Allows you to enter edit mode.
   * @public
   * @return {void}
   */
  public startEdit(): void {
    if (!this.canEdit()) {
      return;
    }
    this.editMode = true;
  }

  /**
   * Allows you to close edit mode.
   * @public
   * @return {void}
   */
  public closeEdit(): void {
    this.editMode = false;
  }

  /**
   * Allows you to collapse/expand HTML sections.
   * @public
   * @param {Event} event Click event
   * @return {void}
   */
  public toggleExpansion(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.expanded = !this.expanded;
  }

  /**
   * Stops the propagation of the given event.
   * @public
   * @param {Event} event The event for which to stop the propagation.
   * @return {void}
   */
  public stopProp(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
  }

  /**
   * When clicking a comment, navigates to the DM page of the comment.
   * The navigation is only done if:
   * - we are on the comments page;
   * - we clicked on a comment about a DM;
   * - we are not in edition mode.
   * @public
   * @returns {Promise<boolean>} `true` when navigation succeeds,
   * or `false` when navigation fails.
   */
  public navigateToDu(): Promise<boolean> {
    if (this.comment.location === CommentLocation.DM && !this.editMode && !this.isOnDm()) {
      return this.router.navigate([
        "pub",
        this.store.publicationID,
        this.store.publicationRevision,
        "du",
        this.comment.dmInfos.dmc
      ]);
    }
    return Promise.resolve(false);
  }

  /**
   * Open the media in a new tab for the browser app.
   * Download the media for the cordova and electron apps.
   * @param event
   * @param mediaId
   * @param fileName
   */
  public openMedia(event: Event, mediaId: string, fileName: string): void {
    event.stopPropagation();
    event.preventDefault();
    if (!this.commentService.canAttachMedia) {
      return;
    }
    this.commentService.openMedia(mediaId, fileName);
  }
}
